import { date } from 'quasar';
import * as chrono from 'chrono-node';
import {
  ChronoResult,
  ChronoTimeObject,
  NormalizeStartEndOptions,
  NormalizeStartEndOutput,
} from '@/types/time';
import dayjs from '@/utils/dayjs';
import words from 'lodash-es/words';
import intersection from 'lodash-es/intersection';
import { ref } from 'vue';
import isEqual from 'lodash-es/isEqual';

export interface NormalizeTimeResult {
  time: string;
  date: string;
}

export const relativeTimeString = (
  date: Date | string,
  dateTimeFormatter: { [key: string]: string }
) => {
  const fromNowText = dayjs(date).fromNow();
  if (
    intersection(words(fromNowText), [
      'day',
      'hour',
      'hours',
      'minute',
      'minutes',
      'second',
      'seconds',
    ]).length
  ) {
    return fromNowText;
  } else {
    return dayjs(date).format(dateTimeFormatter.date);
  }
};

export const formattedDuration = (milliseconds: number) => {
  const sec_num = milliseconds / 1000; // don't forget the second param
  let hours: number | string = Math.floor(sec_num / 3600);
  let minutes: number | string = Math.floor((sec_num - hours * 3600) / 60);
  let seconds: number | string = Math.floor(
    sec_num - hours * 3600 - minutes * 60
  );

  if (hours < 10) {
    hours = '0' + hours;
  }
  if (minutes < 10) {
    minutes = '0' + minutes;
  }
  if (seconds < 10) {
    seconds = '0' + seconds;
  }
  return hours.toLocaleString() + ':' + minutes + ':' + seconds;
};

export const formattedDurationToMilliseconds = (duration: string) => {
  const durationArray = duration.split(':');
  const hours = parseInt(durationArray[0], 10) || 0;
  const minutes = parseInt(durationArray[1], 10) || 0;
  const seconds = parseInt(durationArray[2], 10) || 0;
  return hours * 3600000 + minutes * 60000 + seconds * 1000;
};

export const stringToFormattedDuration = (duration: string): string => {
  return formattedDuration(formattedDurationToMilliseconds(duration));
};

export const timerDuration = (start: Date, end?: Date) => {
  return date.getDateDiff(end || new Date(), start, 'seconds') * 1000;
};

export const timerFormattedDuration = (start: Date, end?: Date) => {
  const milliseconds = timerDuration(start, end);
  if (milliseconds < 0) return '';
  return formattedDuration(milliseconds);
};

export const formattedDate = (
  date: string | Date,
  formatter: { [key: string]: string }
) => {
  return dayjs(date).format(formatter.date);
};

export const formattedTime = (
  time: string | Date,
  formatter: { [key: string]: string }
) => {
  return dayjs(time).format(formatter.time);
};

export const millisecondsToHours = (milliseconds: number) => {
  return milliseconds / 3600000;
};

export const hoursToMilliseconds = (hours: number) => {
  return hours * 3600000;
};

export const secondsToHours = (seconds: number | string) => {
  if (typeof seconds === 'string') seconds = parseFloat(seconds);
  const hours = seconds / 3600;

  if (Number.isInteger(hours)) {
    return hours;
  } else {
    return Number(
      hours.toFixed(hours % 1 === 0 ? 0 : (hours * 10) % 1 === 0 ? 1 : 2)
    );
  }
};

/**
 * Format seconds as HH:MM (or HHH:MM etc for hours > 99)
 * Examples: 0 → "00:00", 3660 → "01:01", 360000 → "100:00"
 */
export const secondsToHHMM = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const paddedHours = hours.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');
  return `${paddedHours}:${paddedMinutes}`;
};

export const millisecondDifference = (
  startTime: string | Date,
  endTime: string | Date
) => {
  const start = dayjs(startTime);
  const end = dayjs(endTime);
  return end.diff(start);
};

const getChronoDateInfo = (
  { knownValues, impliedValues }: ChronoResult,
  formatter: { [key: string]: string }
): NormalizeTimeResult => {
  let hour: number;
  let meridiem: 'am' | 'pm' | null;
  const usingMeridiem = formatter.time.includes('a');

  if (usingMeridiem) {
    meridiem = chrono.Meridiem[
      knownValues.meridiem || impliedValues.meridiem || 0
    ].toLowerCase() as 'am' | 'pm';
    hour = (knownValues.hour || impliedValues.hour || 12) as number; // hour 0 is 12am
    if (hour > 12) {
      hour = hour - 12;
      meridiem = 'pm';
    }
  } else {
    hour = knownValues.hour || impliedValues.hour || 0;
    meridiem = null;
  }
  const minute = (knownValues.minute || impliedValues.minute) as number;
  const zeroPaddedMinute = !minute ? '00' : String(minute).padStart(2, '0');

  const month = ((knownValues.month || impliedValues.month) as number) - 1;

  const dateObj = {
    ...knownValues,
    ...impliedValues,
    month,
  };

  const date = dayjs(
    dateObj as {
      year?: number;
      month?: number;
      day?: number;
      hour?: number;
      minute?: number;
      second?: number;
    }
  ).format(formatter.date);
  const time = dayjs(
    `${date} ${hour}:${zeroPaddedMinute}${usingMeridiem ? ' ' + meridiem : ''}`,
    formatter.fullDateTime
  ).format(formatter.time);

  return {
    time,
    date,
  };
};

export const normalizeTimeInput = (
  timeString: string,
  formatter: { [key: string]: string }
) => {
  const timeObj: ChronoTimeObject | undefined = chrono.parse(
    timeString
  )[0] as unknown as ChronoTimeObject | undefined;
  if (!timeObj || !timeObj.start) return;
  return getChronoDateInfo(timeObj.start, formatter);
};

export const normalizeStartEndInputs = ({
  start,
  end,
  startDate,
  formatter,
}: NormalizeStartEndOptions): NormalizeStartEndOutput | 'INVALID_TIME' => {
  const timeObj: ChronoTimeObject | undefined = chrono.parse(
    `${startDate}, from ${start} to ${end}`
  )[0] as unknown as ChronoTimeObject | undefined;
  if (!timeObj || !timeObj.start || !timeObj.end) return 'INVALID_TIME';
  return {
    start: timeObj.start
      ? getChronoDateInfo(timeObj.start, formatter)
      : undefined,
    end: timeObj.end ? getChronoDateInfo(timeObj.end, formatter) : undefined,
  };
};

export function hoursToMinutes(hours: number) {
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  return {
    hours: rhours,
    minutes: rminutes,
  };
}

/**
 * setInterval implementation with chained setTimeouts. Avoids https://stackoverflow.com/q/6183463
 */
export function useChainedTimeoutInterval(ms: number) {
  const counter = ref(0);
  const isPaused = ref(true);
  let timerId: number | undefined;
  const increment = () => {
    timerId = window.setTimeout(() => {
      ++counter.value;
      if (!isPaused.value) increment();
    }, ms);
  };
  const pause = () => {
    isPaused.value = true;
  };
  const resume = () => {
    isPaused.value = false;
    clearTimeout(timerId);
    timerId = undefined;
    increment();
  };

  resume();
  return {
    counter,
    isPaused,
    pause,
    resume,
  };
}

/**
 * setTimeout utility to use inside async functions for more elegant code
 */
export const timeout = (ms = 0) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export const retry = async (
  task: () => boolean | Promise<boolean>,
  {
    maxRetries = 3,
    interval = 1000,
  }: { maxRetries?: number; interval?: number } = {}
): Promise<boolean> => {
  let result = await task();
  while (maxRetries > 0 && !result) {
    await timeout(interval);
    result = await task();
    maxRetries -= 1;
  }
  return result;
};

/**
 * Utility to format datetime strings where we only want to show the time portion if it's not set to end-of-day.
 * Displaying item due date is a good example of this.
 */
export const formatDateWithOptionalTime = (
  datestring: string,
  dateFormat: string,
  dateTimeFormat: string
) => {
  const dt = dayjs(datestring);
  const isEndOfDay = isEqual(
    [dt.get('hour'), dt.get('minute'), dt.get('second'), dt.get('millisecond')],
    [23, 59, 59, 999]
  );
  return dt.format(isEndOfDay ? dateFormat : dateTimeFormat);
};

export class TimeBasedCache<T> {
  private ttl: number;
  private cache: Map<string, { value: T; timestamp: number }>;

  constructor(ttl: number) {
    this.ttl = ttl;
    this.cache = new Map();
  }

  get(key: string): T | -1 {
    if (!this.cache.has(key)) {
      return -1;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const entry = this.cache.get(key)!;

    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return -1;
    }

    return entry.value;
  }

  put(key: string, value: T) {
    this.cache.set(key, { value, timestamp: Date.now() });
  }

  remove(key: string) {
    this.cache.delete(key);
  }
}
export const hourWithMeridiem = (hour: number) => {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  if (hour < 12) return `${hour}am`;
  return `${hour - 12}pm`;
};

export const localHourToUtcHour = (hour?: number) => {
  if (hour === undefined) return;
  const date = dayjs().hour(hour);
  return date.utc().hour();
};

export const utcHourToLocalHour = (hour?: number) => {
  if (hour === undefined) return;
  const date = dayjs().utc().hour(hour);
  return date.local().hour();
};

export function humanReadableTimeDurationFromHours(hours: number) {
  if (hours < 1) {
    // Less than 1 hour
    const minutes = Math.round(hours * 60);
    return minutes + ' minute' + (minutes !== 1 ? 's' : '');
  } else if (hours < 24) {
    // Less than 1 day
    const wholeHours = Math.floor(hours);
    const remainingMinutes = Math.round((hours - wholeHours) * 60);
    let output = '';
    if (wholeHours > 0) {
      output += wholeHours + ' hour' + (wholeHours !== 1 ? 's' : '');
    }
    if (remainingMinutes > 0) {
      if (wholeHours > 0) {
        output += ' ';
      }
      output +=
        remainingMinutes + ' minute' + (remainingMinutes !== 1 ? 's' : '');
    }
    return output;
  } else {
    // 1 day or more
    const wholeDays = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    let output = wholeDays + ' day' + (wholeDays !== 1 ? 's' : '');
    if (remainingHours > 0) {
      output += ' ' + humanReadableTimeDurationFromHours(remainingHours);
    }
    return output;
  }
}
